// src/components/layout/Footer.js
import styled from 'styled-components';
import { useState, useEffect } from 'react';
import {
  FaFacebookF,
  FaInstagram,
  FaEnvelope,
  FaPhoneAlt,
  FaYoutube,
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { IoMailSharp } from 'react-icons/io5';

import { Input } from './ui';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { isTokenExpired, navigateToLogin } from '../utils';
import { addSubscriber } from '../api/services/mailchimpService';
import { getCustomerProfile } from '../api/services/customerService';

const FooterContent = styled.div`
  padding: 40px 8vw;
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: flex-start;
  max-width: 100vw;
  margin: 0 auto;
  flex-wrap: wrap;
  background-color: #2b388f;
  color: white;

  div {
    margin-bottom: 15px;
  }

  h4 {
    font-size: 18px;
    margin-bottom: 15px;
    font-weight: bold;
  }

  ul {
    list-style: none;
    padding: 0;

    li {
      font-size: 16px;
      margin-bottom: 18px;
      cursor: pointer;
    }
  }

  @media (max-width: 768px) {
    padding: 20px 5vw;
    flex-direction: column;
    align-items: center;
    text-align: center;

    div {
      width: 100% !important;
      margin-bottom: 15px;
    }

    h4 {
      font-size: 16px;
      margin-bottom: 8px;
    }

    ul li {
      font-size: 14px;
      margin-bottom: 10px;
    }
  }

  @media (max-width: 480px) {
    padding: 15px 4vw;

    div {
      margin-bottom: 12px;
    }

    h4 {
      font-size: 15px;
      margin-bottom: 6px;
    }

    ul li {
      font-size: 13px;
      margin-bottom: 6px;
    }
  }
`;

const Newsletter = styled.div`
  display: flex;
  border-radius: 5px;
  overflow: hidden;

  input {
    padding: 10px;
    border: none;
    outline: none;
    flex: 1;
  }

  @media (max-width: 768px) {
    max-width: 300px;
    margin: 0 auto;

    input {
      padding: 6px;
      font-size: 14px;
    }
  }

  @media (max-width: 480px) {
    max-width: 250px;

    input {
      padding: 4px;
      font-size: 13px;
    }
  }
`;

const LetterButton = styled.button`
  background-color: #c19330;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 50px;
  height: 50px;
  border: none;
  &:hover {
    opacity: 0.8;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
  }

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
  }
`;

const SocialIcons = styled.div`
  display: flex;
  margin-top: 10px;

  svg {
    font-size: 20px;
    margin-right: 10px;
    cursor: pointer;
  }

  @media (max-width: 768px) {
    justify-content: center;
    margin-top: 10px;

    svg {
      font-size: 18px;
      margin: 0 5px;
    }
  }

  @media (max-width: 480px) {
    margin-top: 8px;

    svg {
      font-size: 16px;
      margin: 0 4px;
    }
  }
`;

const CopyRightConatiner = styled.div`
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: 1px solid ${(props) => props.theme.colors.subBackground};
  color: ${(props) => props.theme.colors.secondaryText};
  padding: 1rem 0;

  @media (max-width: 768px) {
    padding: 0.6rem 0;
    font-size: 14px;
    text-align: center;
  }

  @media (max-width: 480px) {
    padding: 0.4rem 0;
    font-size: 12px;
  }
`;

const CustomerServiceSection = styled.div`
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: center;

    ul {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    ul li {
      text-align: center;
    }
  }

  @media (max-width: 480px) {
    display: flex;
    flex-direction: column;
    align-items: center;

    ul {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    ul li {
      text-align: center;
    }
  }
`;

const Footer = () => {
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state.userInfo);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customerProfile, setCustomerProfile] = useState(null);

  useEffect(() => {
    if (localStorage.getItem('token') && userInfo.idCustomer) {
      getUserProfileData();
    } else {
      setCustomerProfile(null);
    }
  }, [userInfo.idCustomer]);

  const getUserProfileData = async () => {
    try {
      const result = await getCustomerProfile(userInfo.idCustomer);
      setCustomerProfile(result.data);
    } catch (error) {
      if (error.response?.status !== 401 && error.response?.status !== 403) {
        console.error('Error calling getUserProfile:', error);
      }
    }
  };

  const handleNewsletterSubmit = async () => {
    if (!email.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Get first and last name from customer profile if available
      const firstName = customerProfile?.firstname || '';
      const lastName = customerProfile?.lastname || '';

      await addSubscriber(email, firstName, lastName, ['Website Signup']);

      // Clear the email input after successful subscription
      setEmail('');
      alert('Thank you! You have been added to the newsletter list.');
    } catch (error) {
      alert('Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer>
      <FooterContent>
        <div style={{ width: '25%' }}>
          <h4>Newsletter</h4>
          <Newsletter>
            <Input
              placeholder='Enter your email here...'
              value={email}
              onChange={(value) => setEmail(value)}
              disabled={isSubmitting}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleNewsletterSubmit();
                }
              }}
            />
            <LetterButton
              onClick={handleNewsletterSubmit}
              disabled={isSubmitting}
            >
              <FaEnvelope />
            </LetterButton>
          </Newsletter>
        </div>
        <div>
          <h4>INFORMATION</h4>
          <ul>
            <li
              onClick={() => {
                navigate('/contactus');
              }}
            >
              Contact Us
            </li>
            <li
              onClick={() => {
                navigate('/aboutus');
              }}
            >
              About Us
            </li>
            <li
              onClick={() => {
                navigate('/accessibility');
              }}
            >
              Accessibility
            </li>
            <li
              onClick={() => {
                const token = localStorage.getItem('token');
                if (!token || isTokenExpired(token)) {
                  navigateToLogin(navigate, '/faq');
                } else {
                  navigate('/faq');
                }
              }}
            >
              FAQ
            </li>
            <li
              onClick={() => {
                const token = localStorage.getItem('token');
                if (!token || isTokenExpired(token)) {
                  navigateToLogin(navigate, '/ecatalog');
                } else {
                  navigate('/ecatalog');
                }
              }}
            >
              E-Catalogs
            </li>
            <li
              onClick={() => {
                const token = localStorage.getItem('token');
                if (!token || isTokenExpired(token)) {
                  navigateToLogin(navigate, '/career');
                } else {
                  navigate('/career');
                }
              }}
            >
              Career
            </li>
          </ul>
        </div>
        <div>
          <h4>MY ACCOUNT</h4>
          <ul>
            {/* <li
              onClick={() => {
                if (userInfo.userId != 0) {
                  navigate('/myaccount/customer');
                } else {
                  navigateToLogin(navigate, '/myaccount/customer');
                }
              }}
            >
              My Account
            </li> */}
            <li
              onClick={() => {
                if (userInfo.userId != 0) {
                  navigate('/myaccount/orders');
                } else {
                  navigateToLogin(navigate, '/myaccount/orders');
                }
              }}
            >
              Web Orders History
            </li>
          </ul>
        </div>
        <CustomerServiceSection>
          <h4>CUSTOMER SERVICE</h4>
          <ul>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <FaPhoneAlt />
              {'323-582-5858'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <IoMailSharp />
              cs@edencorp.com
            </div>
          </ul>
        </CustomerServiceSection>
        <div>
          <h4>FOLLOW US</h4>
          <SocialIcons>
            <a
              href='https://www.facebook.com/edenmarketingcorp'
              target='_blank'
              rel='noopener noreferrer'
            >
              <FaFacebookF />
            </a>
            <a
              href='https://www.instagram.com/edenmarketingcorp/'
              target='_blank'
              rel='noopener noreferrer'
            >
              <FaInstagram />
            </a>
            <a
              href='https://x.com/EdenMCorp'
              target='_blank'
              rel='noopener noreferrer'
            >
              <FaXTwitter />
            </a>
            <a
              href='https://www.youtube.com/@Eden-Marketing-Corp'
              target='_blank'
              rel='noopener noreferrer'
            >
              <FaYoutube />
            </a>
          </SocialIcons>
        </div>
      </FooterContent>
      <CopyRightConatiner>
        Copyright © 2025 EdenCorp. All rights reserved.
      </CopyRightConatiner>
    </footer>
  );
};

export default Footer;
